import { Formik } from 'formik';
import { useCallback } from 'react';
import {
  Dialog,
  Details,
  FormFooter,
  useNavigate,
  ButtonTypes,
  ButtonColors,
  handleMutation,
  StandardButton,
  StandardButtonWithRef,
} from '@gv/triage-components';

import { useDeleteMutation } from 'store/api/clients';

import * as Styles from '../styles';
import * as CommonStyles from '../../styles';

import { useClientForm } from './use-client-form';
import { FormProps, CommonClientPayload } from './types';

const Form = ({
  isEdit,
  clientId,
  onSubmit,
  onDelete,
  hospitalId,
  initialValues,
  allowDeletion = true,
}: FormProps): JSX.Element => {
  const navigate = useNavigate();

  const [deleteClient, mutation] = useDeleteMutation();

  const onSuccess = useCallback(() => {
    navigate(-2);
    if (onDelete && clientId) {
      onDelete(clientId);
    }
  }, []);

  const { fields, validate, pageDetails, validationSchema } = useClientForm({
    hospitalId,
    isEdit: isEdit ?? false,
    isTimezoneFieldAvailable: true,
  });

  handleMutation({ ...mutation, onSuccess });

  return (
    <Details {...pageDetails}>
      <Formik<CommonClientPayload>
        enableReinitialize
        onSubmit={onSubmit}
        validate={validate}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {(formikProps) => {
          const { dirty, isSubmitting } = formikProps;
          return (
            <CommonStyles.Form>
              <CommonStyles.Inner>
                <Styles.BackButton
                  text={isEdit ? 'Edit Client' : 'New Client'}
                />

                {fields(formikProps)}
              </CommonStyles.Inner>

              <FormFooter
                right={
                  <>
                    <StandardButton
                      text="Cancel"
                      onClick={() => navigate(-1)}
                      colorType={ButtonColors.Secondary}
                    />

                    <StandardButton
                      text="Save"
                      type={ButtonTypes.Submit}
                      disabled={isSubmitting || !dirty}
                    />
                  </>
                }
                left={
                  isEdit &&
                  clientId &&
                  allowDeletion && (
                    <Dialog
                      confirmText="Delete"
                      title="Delete client"
                      text="Are you sure that you want to delete?"
                      onConfirm={() => deleteClient(Number(clientId))}
                      trigger={
                        <StandardButtonWithRef
                          text="Delete client"
                          colorType={ButtonColors.LightRed}
                        />
                      }
                    />
                  )
                }
              />
            </CommonStyles.Form>
          );
        }}
      </Formik>
    </Details>
  );
};

export { Form };
